/**
 * User Form Modal Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Modal dialog for creating and editing users.
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { UserListItem, UserRole, UserCreateData, UserUpdateData } from '../../../types/admin';
import UserRoleSelect from './UserRoleSelect';
import { Button } from '../../ui/button';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UserCreateData | UserUpdateData) => Promise<void>;
  user?: UserListItem | null;
  isLoading?: boolean;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  isLoading = false,
}) => {
  const isEdit = !!user;

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'advisor' as UserRole,
    send_invitation: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setFormData({
          email: user.email,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          role: user.role,
          send_invitation: false,
        });
      } else {
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          role: 'advisor',
          send_invitation: true,
        });
      }
      setErrors({});
    }
  }, [isOpen, user]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!isEdit) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (isEdit) {
      await onSubmit({
        first_name: formData.first_name || undefined,
        last_name: formData.last_name || undefined,
      });
    } else {
      await onSubmit({
        email: formData.email,
        first_name: formData.first_name || undefined,
        last_name: formData.last_name || undefined,
        role: formData.role,
        send_invitation: formData.send_invitation,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit User' : 'Create User'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                  {!isEdit && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={isEdit}
                  className={`
                    w-full px-3 py-2 border rounded-lg text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    disabled:bg-gray-50 disabled:text-gray-500
                    ${errors.email ? 'border-red-300' : 'border-gray-300'}
                  `}
                  placeholder="user@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
                  placeholder="John"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
                  placeholder="Doe"
                />
              </div>

              {/* Role (only for create) */}
              {!isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <UserRoleSelect
                    value={formData.role}
                    onChange={(role) => setFormData({ ...formData, role })}
                    showDescription
                  />
                </div>
              )}

              {/* Send invitation checkbox (only for create) */}
              {!isEdit && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="send_invitation"
                    checked={formData.send_invitation}
                    onChange={(e) => setFormData({ ...formData, send_invitation: e.target.checked })}
                    className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
                  />
                  <label htmlFor="send_invitation" className="text-sm text-gray-700">
                    Send invitation email
                  </label>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create User'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;
